"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { getSubmissionCount, getTrophyArtworkCount } from "@/helpers/artist-activity.helpers";
import { useArtistPreviewModal, type ArtistPreviewTab } from "@/hooks/useArtistPreviewModal";
import { closeAllCustomTooltips } from "@/helpers/tooltip.helpers";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { CurationWaveBadge } from "./CurationWaveBadge";
import type { ApiProfileClassification } from "@/generated/models/ApiProfileClassification";
import { getProfileWaveIdentity } from "@/hooks/useProfileWave";
import { useRouter } from "next/navigation";
interface DropAuthorBadgesProfile {
  readonly id?: string | null;
  readonly handle?: string | null;
  readonly pfp?: string | null;
  readonly banner1_color?: string | null;
  readonly banner2_color?: string | null;
  readonly cic?: number;
  readonly rep?: number;
  readonly tdh?: number;
  readonly tdh_rate?: number;
  readonly xtdh?: number;
  readonly xtdh_rate?: number;
  readonly level?: number;
  readonly primary_address?: string | null;
  readonly primary_wallet?: string | null;
  readonly archived?: boolean;
  readonly active_main_stage_submission_ids?: readonly string[] | null;
  readonly winner_main_stage_drop_ids?: readonly string[] | null;
  readonly artist_of_prevote_cards?: readonly number[] | null;
  readonly profile_wave_id?: string | null;
  readonly profile_wave_name?: string | null;
  readonly profile_wave_pfp?: string | null;
  readonly is_wave_creator?: boolean | null;
  readonly badges?: {
    readonly artist_of_main_stage_submissions?: number | null;
    readonly artist_of_memes?: number | null;
    readonly profile_wave_id?: string | null;
    readonly profile_wave_name?: string | null;
    readonly profile_wave_pfp?: string | null;
  } | null;
  readonly classification: ApiProfileClassification;
  readonly sub_classification: string | null;
}
type ApiProfileMinWithAuthorBadges = ApiProfileMin & {
  readonly badges?: DropAuthorBadgesProfile["badges"];
};
interface DropAuthorBadgesProps {
  readonly profile: DropAuthorBadgesProfile;
  readonly tooltipIdPrefix?: string | undefined;
  readonly className?: string | undefined;
  readonly size?: "default" | "compact" | undefined;
  readonly showProfileWaveBadge?: boolean | undefined;
  readonly onArtistPreviewOpen?: ((params: {
    readonly user: ApiProfileMin;
    readonly initialTab: ArtistPreviewTab;
  }) => void) | undefined;
}
const DEFAULT_CONTAINER_CLASS = "tw-inline-flex tw-items-center tw-gap-x-1.5";
const getTrimmedText = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
};
const getProfileWaveId = (profile: DropAuthorBadgesProfile): string | null => getTrimmedText(profile.profile_wave_id) ?? getTrimmedText(profile.badges?.profile_wave_id);
const getProfileWaveName = (profile: DropAuthorBadgesProfile): string | null => getTrimmedText(profile.profile_wave_name) ?? getTrimmedText(profile.badges?.profile_wave_name);
const getProfileWavePfp = (profile: DropAuthorBadgesProfile): string | null => getTrimmedText(profile.profile_wave_pfp) ?? getTrimmedText(profile.badges?.profile_wave_pfp);
const copyArray = <T,>(value?: readonly T[] | null): T[] => value !== null && value !== undefined ? [...value] : [];
const toApiProfileMin = (profile: DropAuthorBadgesProfile): ApiProfileMinWithAuthorBadges => {
  const primaryAddress = profile.primary_address ?? profile.primary_wallet ?? "";
  const fallbackId = primaryAddress;
  const profileWaveId = getProfileWaveId(profile);
  return {
    id: profile.id ?? fallbackId,
    handle: profile.handle ?? null,
    pfp: profile.pfp ?? null,
    banner1_color: profile.banner1_color ?? null,
    banner2_color: profile.banner2_color ?? null,
    cic: profile.cic ?? 0,
    rep: profile.rep ?? 0,
    tdh: profile.tdh ?? 0,
    tdh_rate: profile.tdh_rate ?? 0,
    xtdh: profile.xtdh ?? 0,
    xtdh_rate: profile.xtdh_rate ?? 0,
    level: profile.level ?? 0,
    primary_address: primaryAddress,
    subscribed_actions: [],
    archived: profile.archived ?? false,
    active_main_stage_submission_ids: copyArray(profile.active_main_stage_submission_ids),
    winner_main_stage_drop_ids: copyArray(profile.winner_main_stage_drop_ids),
    artist_of_prevote_cards: copyArray(profile.artist_of_prevote_cards),
    profile_wave_id: profileWaveId,
    is_wave_creator: profile.is_wave_creator === true,
    classification: profile.classification,
    sub_classification: profile.sub_classification,
    badges: profile.badges
  };
};
export const DropAuthorBadges: React.FC<DropAuthorBadgesProps> = t0 => {
  const $ = _c(48);
  const {
    profile,
    tooltipIdPrefix: t1,
    className: t2,
    size: t3,
    showProfileWaveBadge: t4,
    onArtistPreviewOpen
  } = t0;
  const tooltipIdPrefix = t1 === undefined ? "author-badges" : t1;
  const className = t2 === undefined ? DEFAULT_CONTAINER_CLASS : t2;
  const size = t3 === undefined ? "default" : t3;
  const showProfileWaveBadge = t4 === undefined ? true : t4;
  const router = useRouter();
  let t5;
  if ($[0] !== profile) {
    t5 = getProfileWaveId(profile);
    $[0] = profile;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const profileWaveId = t5;
  const submissionCount = getSubmissionCount(profile);
  const trophyCount = getTrophyArtworkCount(profile);
  const hasActivityBadge = submissionCount > 0 || trophyCount > 0;
  let t6;
  if ($[2] !== profile) {
    t6 = getProfileWaveName(profile);
    $[2] = profile;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const profileWaveName = t6;
  let t7;
  if ($[4] !== profile) {
    t7 = getProfileWavePfp(profile);
    $[4] = profile;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const profileWavePfp = t7;
  const hasProfileWaveBadge = showProfileWaveBadge && profileWaveId !== null;
  let t8;
  if ($[6] !== profile) {
    t8 = getProfileWaveIdentity(profile);
    $[6] = profile;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const profileWaveIdentity = t8;
  let t9;
  if ($[8] !== profile) {
    t9 = toApiProfileMin(profile);
    $[8] = profile;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const modalUser = t9;
  const {
    isModalOpen: isArtistPreviewOpen,
    activeTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleTabChange: handleArtistTabChange,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  let t10;
  if ($[10] !== handleArtistBadgeClick || $[11] !== modalUser || $[12] !== onArtistPreviewOpen) {
    t10 = tab => {
      closeAllCustomTooltips();
      if (onArtistPreviewOpen) {
        onArtistPreviewOpen({
          user: modalUser,
          initialTab: tab
        });
        return;
      }
      handleArtistBadgeClick(tab);
    };
    $[10] = handleArtistBadgeClick;
    $[11] = modalUser;
    $[12] = onArtistPreviewOpen;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const onArtistBadgeClick = t10;
  let t11;
  if ($[14] !== profileWaveId || $[15] !== router) {
    t11 = () => {
      closeAllCustomTooltips();
      if (!profileWaveId) {
        return;
      }
      router.push(getWaveRoute({
        waveId: profileWaveId,
        isDirectMessage: false,
        isApp: false
      }));
    };
    $[14] = profileWaveId;
    $[15] = router;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const onProfileWaveBadgeClick = t11;
  if (!hasActivityBadge && !hasProfileWaveBadge) {
    return null;
  }
  let t12;
  if ($[17] !== hasActivityBadge || $[18] !== onArtistBadgeClick || $[19] !== size || $[20] !== submissionCount || $[21] !== tooltipIdPrefix || $[22] !== trophyCount) {
    t12 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} trophyCount={trophyCount} onBadgeClick={onArtistBadgeClick} tooltipId={`${tooltipIdPrefix}-activity`} size={size} />;
    $[17] = hasActivityBadge;
    $[18] = onArtistBadgeClick;
    $[19] = size;
    $[20] = submissionCount;
    $[21] = tooltipIdPrefix;
    $[22] = trophyCount;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== hasProfileWaveBadge || $[25] !== onProfileWaveBadgeClick || $[26] !== profileWaveId || $[27] !== profileWaveIdentity || $[28] !== profileWaveName || $[29] !== profileWavePfp || $[30] !== size || $[31] !== tooltipIdPrefix) {
    t13 = hasProfileWaveBadge && <CurationWaveBadge waveId={profileWaveId} tooltipId={`${tooltipIdPrefix}-profile-wave`} onBadgeClick={onProfileWaveBadgeClick} size={size} profileIdentity={profileWaveIdentity} waveName={profileWaveName} wavePfp={profileWavePfp} />;
    $[24] = hasProfileWaveBadge;
    $[25] = onProfileWaveBadgeClick;
    $[26] = profileWaveId;
    $[27] = profileWaveIdentity;
    $[28] = profileWaveName;
    $[29] = profileWavePfp;
    $[30] = size;
    $[31] = tooltipIdPrefix;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== className || $[34] !== t12 || $[35] !== t13) {
    t14 = <div className={className}>{t12}{t13}</div>;
    $[33] = className;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== activeTab || $[38] !== handleArtistModalClose || $[39] !== handleArtistTabChange || $[40] !== hasActivityBadge || $[41] !== isArtistPreviewOpen || $[42] !== modalUser || $[43] !== onArtistPreviewOpen) {
    t15 = hasActivityBadge && !onArtistPreviewOpen && <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={modalUser} activeTab={activeTab} onTabChange={handleArtistTabChange} />;
    $[37] = activeTab;
    $[38] = handleArtistModalClose;
    $[39] = handleArtistTabChange;
    $[40] = hasActivityBadge;
    $[41] = isArtistPreviewOpen;
    $[42] = modalUser;
    $[43] = onArtistPreviewOpen;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t14 || $[46] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
};